import type { CSSProperties, MouseEvent as ReactMouseEvent } from "react";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { type DashCard, type DashMetricStyle, CALC_LABELS, CHART_LABELS, GROUPBY_LABELS, fmtKpi, cardMetrics, metricSpecKey, computeItemValues, isValueItem } from "../../lib/dashboard";
import { BarChart } from "../charts/BarChart";
import { LineChart } from "../charts/LineChart";
import { DonutChart } from "../charts/DonutChart";

interface Meta { formName: string; unit: string | null; label: string }
type AggResult = Record<string, { value: number | null; count: number }>;

/** Kullanıcı stilini CSS'e çevir — yalnız AÇIKÇA seçilenler inline yazılır, gerisi sınıf varsayılanında kalır. */
function styleCss(st: DashMetricStyle | null | undefined): CSSProperties {
  const s = st ?? {};
  return {
    fontSize: s.fontSize ? `${s.fontSize}px` : undefined,
    color: s.color || undefined,
    fontWeight: s.fontWeight,
    textTransform: s.caps === true ? "uppercase" : s.caps === false ? "none" : undefined,
  };
}

interface Props {
  card: DashCard;
  fieldMeta: (formId: string, fieldId: string) => Meta;
  color: string;
  agg: AggResult;
  aggLoading: boolean;
  dragging: boolean;
  onBeginDrag: (e: ReactMouseEvent) => void;
  onBeginResize: (e: ReactMouseEvent) => void;
  onEdit: () => void;
  onDelete: () => void;
}

/** Panodaki tek kart: kind='kpi' → bir veya çok metrik (her biri kendi stiliyle); kind='chart' → SVG grafik. */
export function DashCardView({ card, fieldMeta, color, agg, aggLoading, dragging, onBeginDrag, onBeginResize, onEdit, onDelete }: Props) {
  const isChart = card.kind === "chart";
  // series yalnız grafik kartında koşar (KPI'de enabled=false → fetch yok, hook kuralı korunur).
  const seriesQ = trpc.form.dashboard.series.useQuery(
    { formTemplateId: card.formTemplateId, fieldId: card.fieldId, calc: card.calc, groupBy: card.groupBy ?? "day", from: card.from, to: card.to },
    { enabled: isChart && !!card.groupBy, staleTime: 15_000 },
  );
  const chartData = seriesQ.data ?? [];
  const chartMeta = fieldMeta(card.formTemplateId, card.fieldId);

  const head = (
    <div className="head">
      <span className="dash-grip" aria-hidden="true"><Icon name="gripVertical" size={13} /></span>
      <span className="lbl" title={card.title} style={{ ...styleCss(card.titleStyle), textAlign: card.titleStyle?.align }}>{card.title}</span>
      <span className="dash-card-acts">
        <button type="button" className="dash-iact" aria-label="Düzenle" title="Düzenle" onClick={onEdit}><Icon name="edit" size={13} /></button>
        <button type="button" className="dash-iact danger" aria-label="Sil" title="Sil" onClick={onDelete}><Icon name="trash" size={13} /></button>
      </span>
    </div>
  );
  const resize = <span className="dash-resize" onMouseDown={onBeginResize} aria-hidden="true" title="Boyutlandır" />;
  const placement = { ["--kpi-color" as string]: color, gridColumn: `${card.x + 1} / span ${card.w}`, gridRow: `${card.y + 1} / span ${card.h}` } as CSSProperties;

  if (isChart) {
    return (
      <div className={`dash-kpi dash-card dash-chart-card${dragging ? " dragging" : ""}`} style={placement} onMouseDown={onBeginDrag}>
        {head}
        <div className="dash-chart-body">
          {seriesQ.isLoading ? <div className="dash-chart-empty">Yükleniyor…</div>
            : chartData.length === 0 ? <div className="dash-chart-empty">Veri yok</div>
            : card.chartType === "bar" ? <BarChart data={chartData} color={color} unit={chartMeta.unit} />
            : card.chartType === "donut" ? <DonutChart data={chartData} unit={chartMeta.unit} />
            : <LineChart data={chartData} color={color} area={card.chartType === "area"} unit={chartMeta.unit} />}
        </div>
        <div className="sub" title={`${chartMeta.formName} · ${chartMeta.label}`}>
          {chartMeta.formName} · {chartMeta.label} · {CALC_LABELS[card.calc]} · {card.chartType ? CHART_LABELS[card.chartType] : ""}{card.groupBy ? " · " + GROUPBY_LABELS[card.groupBy] : ""}
        </div>
        {resize}
      </div>
    );
  }

  // KPI kartı — öğeler: form değeri (metric), sabit sayı (static), bilgi notu (note), başlık (heading), çizgi (divider).
  const items = cardMetrics(card);
  // Öğe değerleri TEK yerde çözülür: metric→aggregate, static→sabit, formula→kart-içi hesap
  // (formül operandları farklı form/tesisten metriklere bakabilir; bağımlılıklar iteratif çözülür).
  const itemValues = computeItemValues(items, (m) =>
    (m.type ?? "metric") === "static" ? (m.value ?? null) : (agg[metricSpecKey(card.id, m.id)]?.value ?? null));
  const valueItems = items.filter(isValueItem).length;
  const showLabels = valueItems > 1 || items.length > 1; // tek değer + hiç yapısal yoksa etiket kart başlığıyla tekrar → gizle
  const alignMap = { left: "flex-start", center: "center", right: "flex-end" } as const;
  return (
    <div className={`dash-kpi dash-card dash-kpi-card${dragging ? " dragging" : ""}`} style={placement} onMouseDown={onBeginDrag}>
      {head}
      <div className="dash-kpi-metrics">
        {items.map((m) => {
          const type = m.type ?? "metric";
          const st = m.style ?? {};
          // Yan yana yerleşimde değerin doğal yeri sağ (referans kart deseni); alt alta yerleşimde sol.
          const align = st.align ?? ((m.layout ?? "stack") === "row" ? "right" : "left");
          if (type === "divider") {
            return <div key={m.id} className="dash-item-divider" style={st.color ? { background: st.color } : undefined} />;
          }
          if (type === "note") {
            return <div key={m.id} className="dash-item-note" style={{ textAlign: align, color: st.color || undefined, fontSize: st.fontSize ? `${st.fontSize}px` : undefined }}>{m.text}</div>;
          }
          if (type === "heading") {
            return <div key={m.id} className="dash-item-heading" style={{ textAlign: align, color: st.color || undefined, fontSize: st.fontSize ? `${st.fontSize}px` : undefined, fontWeight: st.fontWeight }}>{m.text}</div>;
          }
          // metric | static | formula
          const isRow = (m.layout ?? "stack") === "row";
          const valStyle = styleCss(m.style);
          const lblStyle = styleCss(m.labelStyle);
          let valueText: string, unit: string | null, label: string;
          if (type === "static") {
            valueText = fmtKpi(m.value);
            unit = m.unit?.trim() || null;
            label = m.label?.trim() || "Sabit Değer";
          } else if (type === "formula") {
            // Formül metriklere bağlıysa aggregate gelene kadar bekle (yoksa kısa süre "—" yanıp söner).
            const dependsOnMetric = items.some((x) => (x.type ?? "metric") === "metric");
            const pending = aggLoading && dependsOnMetric;
            const fv = itemValues[m.id] ?? null;
            valueText = pending ? "…" : fmtKpi(fv);
            // Birim yalnız GERÇEK bir değer varken basılır — metrik dalıyla aynı kural ("…%" / "—%" olmasın).
            unit = !pending && fv != null ? (m.unit?.trim() || null) : null;
            label = m.label?.trim() || "Hesaplanan";
          } else {
            const mm = fieldMeta(m.formTemplateId ?? "", m.fieldId ?? "");
            const r = agg[metricSpecKey(card.id, m.id)];
            valueText = aggLoading ? "…" : fmtKpi(r?.value);
            unit = mm.unit && r?.value != null && m.calc !== "count" ? mm.unit : null;
            label = m.label?.trim() || `${mm.label || "—"} · ${m.calc ? CALC_LABELS[m.calc] : ""}`;
          }
          // Yan yana yerleşimde etiket her zaman görünür (kullanıcı çifti bilerek istedi).
          const labelEl = showLabels || isRow ? <span className="dash-metric-lbl" style={lblStyle} title={label}>{label}</span> : null;
          const valueEl = (
            <span className="dash-metric-val" style={valStyle}>
              {valueText}
              {unit ? <span className="dash-metric-unit">{unit}</span> : null}
            </span>
          );
          if (isRow) {
            // Etiket solda · değer sağda (taban çizgisi hizalı); align değerin kalan alandaki yerini belirler.
            return (
              <div className="dash-metric is-row" key={m.id}>
                {labelEl}
                <span className="dash-metric-valwrap" style={{ textAlign: align }}>{valueEl}</span>
              </div>
            );
          }
          return (
            <div className="dash-metric" key={m.id} style={{ alignItems: alignMap[align] }}>
              {labelEl}
              {valueEl}
            </div>
          );
        })}
      </div>
      {resize}
    </div>
  );
}
